/** Structured policy errors are expected rejections, not network exceptions. */
export function isUsPickupDeadlineError(error: unknown): boolean {
  const value = error as { code?: unknown; body?: { code?: unknown; error?: unknown }; message?: unknown } | null;
  const code = String(value?.code ?? value?.body?.code ?? value?.body?.error ?? "");
  return ["US_SELF_DRIVE_CHECKOUT_CLOSED", "US_SELF_DRIVE_CONFIRMATION_CLOSED",
    "US_PICKUP_TIMEZONE_UNRESOLVED", "us_self_drive_confirmation_cutoff_closed"].includes(code);
}

const copy: Record<string, { soon: string; timezone: string }> = {
  en: {
    soon: "Your selected pickup is too soon to complete a new US self-drive booking. Choose a later pickup time, more than four hours from now.",
    timezone: "We couldn't establish an unambiguous pickup time in this listing's timezone. Choose another pickup time or contact support.",
  },
  da: {
    soon: "Det valgte afhentningstidspunkt er for tæt på til at gennemføre en ny selvkørselsbooking i USA. Vælg et senere tidspunkt, mere end fire timer fra nu.",
    timezone: "Vi kunne ikke fastlægge et entydigt afhentningstidspunkt i bilens tidszone. Vælg et andet tidspunkt, eller kontakt support.",
  },
  de: {
    soon: "Die gewählte Abholzeit ist zu früh, um eine neue Selbstfahrerbuchung in den USA abzuschließen. Wählen Sie eine spätere Abholzeit, mehr als vier Stunden ab jetzt.",
    timezone: "Die Abholzeit konnte in der Zeitzone dieses Fahrzeugs nicht eindeutig bestimmt werden. Wählen Sie eine andere Zeit oder kontaktieren Sie den Support.",
  },
  es: {
    soon: "La recogida seleccionada es demasiado pronto para completar una nueva reserva sin conductor en EE. UU. Elige una hora posterior, a más de cuatro horas de ahora.",
    timezone: "No pudimos determinar una hora de recogida inequívoca en la zona horaria de este vehículo. Elige otra hora o contacta con soporte.",
  },
  fr: {
    soon: "L'heure de prise en charge choisie est trop proche pour finaliser une nouvelle réservation sans chauffeur aux États-Unis. Choisissez une heure plus tardive, dans plus de quatre heures.",
    timezone: "Nous n'avons pas pu déterminer une heure de prise en charge sans ambiguïté dans le fuseau horaire de ce véhicule. Choisissez une autre heure ou contactez l'assistance.",
  },
  it: {
    soon: "L'orario di ritiro scelto è troppo vicino per completare una nuova prenotazione senza conducente negli Stati Uniti. Scegli un orario successivo, tra più di quattro ore.",
    timezone: "Non è stato possibile determinare un orario di ritiro univoco nel fuso orario di questo veicolo. Scegli un altro orario o contatta l'assistenza.",
  },
  nl: {
    soon: "De gekozen ophaaltijd is te dichtbij om een nieuwe zelfrijboeking in de VS af te ronden. Kies een later tijdstip, meer dan vier uur vanaf nu.",
    timezone: "We konden geen eenduidige ophaaltijd bepalen in de tijdzone van deze auto. Kies een ander tijdstip of neem contact op met support.",
  },
  no: {
    soon: "Det valgte hentetidspunktet er for nært til å fullføre en ny selvkjøringsbestilling i USA. Velg et senere tidspunkt, mer enn fire timer fra nå.",
    timezone: "Vi kunne ikke fastslå et entydig hentetidspunkt i bilens tidssone. Velg et annet tidspunkt eller kontakt kundestøtte.",
  },
  sv: {
    soon: "Den valda hämtningstiden är för nära för att slutföra en ny bokning med egen körning i USA. Välj en senare tid, mer än fyra timmar från nu.",
    timezone: "Vi kunde inte fastställa en entydig hämtningstid i bilens tidszon. Välj en annan tid eller kontakta support.",
  },
};

/** Share wording across web/native without relying on native i18next setup. */
export function usPickupDeadlineMessage(error: unknown, locale = "en"): string {
  const value = error as { code?: unknown; body?: { code?: unknown; error?: unknown } } | null;
  const code = String(value?.code ?? value?.body?.code ?? value?.body?.error ?? "");
  const language = locale.toLowerCase().split(/[-_]/)[0];
  const localized = copy[language] ?? copy.en;
  return code === "US_PICKUP_TIMEZONE_UNRESOLVED" ? localized.timezone : localized.soon;
}